/* eslint-disable react-hooks/exhaustive-deps  */
/* eslint-disable array-callback-return  */
import React, { useRef, useCallback, memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { getPostValidationError, getPreValidationError } from 'app/utils';
import { Area, Label, Button, Checkbox, Error } from 'app/components';
import { useConfig } from 'app/hooks';
import { FieldRenderProps } from '../DataInputs';

const propTypes = {
  form: PropTypes.objectOf(PropTypes.any).isRequired,
  errors: PropTypes.arrayOf(PropTypes.object),
  dataFields: PropTypes.shape({
    message: PropTypes.shape(FieldRenderProps),
    attachment: PropTypes.shape(FieldRenderProps),
    attachmentName: PropTypes.shape(FieldRenderProps),
    terms: PropTypes.shape(FieldRenderProps)
  }).isRequired,
  submitFailed: PropTypes.bool.isRequired,
  legalInfo: PropTypes.arrayOf(
    PropTypes.shape({
      doc: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired
    })
  ).isRequired,
  notify: PropTypes.func.isRequired,
  setLoader: PropTypes.func.isRequired
};

const defaultProps = {
  errors: []
};

const imageValidate = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];

const validatingTermsConditions = field => {
  return (
    !field.input.value.includes('terms and conditions') ||
    !field.input.value.includes('privacy policy')
  );
};

const AdditionalInfo = ({
  form,
  errors,
  dataFields: { message, attachment, attachmentName, terms },
  submitFailed,
  legalInfo,
  notify,
  setLoader
}) => {
  const [t] = useTranslation();
  const inputRef = useRef();
  const {
    media: { docsPrefix }
  } = useConfig();

  const [legalInfoList, setLegalInfoList] = useState([]);

  useEffect(() => {
    if (legalInfo?.length) {
      const list = legalInfo.filter(item => process.env.REACT_APP_COUNTRY === "srb" ? [6, 1].some(value => value === +item.docType) : process.env.REACT_APP_COUNTRY === "mne" ? [3, 5].some(value => value === +item.docType) : [1, 3].some(value => value === +item.docType));

      setLegalInfoList(list);
    }
  }, [legalInfo]);

  const handleTermsChange = useCallback(
    item => {
      const termList = [...terms.input.value];

      if (Array.isArray(termList)) {
        if (termList.includes(item)) {
          termList.splice(termList.indexOf(item), 1);
        } else {
          termList.push(item);
        }
      } else {
        termList.push(item);
      }
      form.change(terms.input.name, termList);
    },
    [terms.input.value]
  );

  const handleUploadAttachment = e => {
    setLoader(true);
    const file = e.target.files[0];

    if (Math.round(file.size / 1024) > 1024) {
      setLoader(false);
      notify('warning', t('cinema_rent_inquiry_form.image_error', { num: 1 }));

      return;
    }

    if (!imageValidate.includes(file.type)) {
      setLoader(false);
      notify('warning', t('cinema_rent_inquiry_form.image_format_error'));

      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      setLoader(false);
      form.change(attachmentName.input.name, file.name);
      form.change(attachment.input.name, reader.result);
    };

    reader.readAsDataURL(file);
  };

  const screenWidth = window.innerWidth;
  const largeScreenCols = screenWidth <= 768 ? 32 : 64;
  const largeScreenRows = screenWidth <= 768 ? 5 : 10;

  return (
    <div className="cinema-rent__additional-info-wrapper">
      <h2>{t("cinema_rent_inquiry_form.additional_information")}</h2>
      <div className="cinema-rent__additional-info-message">
        <Label>{t("cinema_rent_inquiry_form.message")}</Label>
        <Area
          className="lg-screen-area"
          // cols={64}
          // rows={10}
          cols={largeScreenCols}
          rows={largeScreenRows}
          id={message.input.name}
          {...message.input}
          error={
            getPreValidationError(message, t) ||
            (getPostValidationError(errors, message.input.name, t) && message)
          }
        />
      </div>
      <h3 className="cinema-rent__attachment-info">
        {t('cinema_rent_inquiry_form.attachment_rule', { num: 1 })}
      </h3>
      <div className="cinema-rent__attachment-wrapper">
        <input
          ref={inputRef}
          type="file"
          className="cinema-rent__attachment-input"
          id={attachment.input.name}
          onChange={e => handleUploadAttachment(e)}
        />
        <Button
          isPrimary
          className="cinema-rent__attachment-btn"
          onClick={() => inputRef.current.click()}
        >
          {t('cinema_rent_inquiry_form.attachment_upload')}
        </Button>
        <h3>{attachmentName.input.value}</h3>
      </div>
      <div className="cinema-rent__terms-wrapper">
        {process.env.REACT_APP_COUNTRY === "hrv" ?
          "" :
          (<Label className="cinema-rent__terms-checkbox">
            <Checkbox
              id="terms and conditions"
              value={terms.input.value?.includes('terms and conditions')}
              checked={terms.input.value?.includes('terms and conditions')}
              onChange={() => handleTermsChange('terms and conditions')}
            />
            <h3>
              {t('cinema_rent_inquiry_form.yes_accept')} *{' '}
              <a
                href={docsPrefix(legalInfoList[process.env.REACT_APP_COUNTRY === "srb" ? 1 : 0]?.doc)}
                className="cinema-rent__terms-link"
                rel="noopener noreferrer"
                target="_blank"
              >
                {t('common.read_more')}
              </a>
            </h3>
          </Label>)}
        <Label className="cinema-rent__terms-checkbox">
          <Checkbox
            id="privacy policy"
            value={terms.input.value?.includes('privacy policy')}
            checked={terms.input.value?.includes('privacy policy')}
            onChange={() => handleTermsChange('privacy policy')}
          />
          <h3>
            {t('cinema_rent_inquiry_form.yes_note')} *{' '}
            <a
              href={docsPrefix(legalInfoList[process.env.REACT_APP_COUNTRY === "srb" ? 0 : 1]?.doc)}
              className="cinema-rent__terms-link"
              rel="noopener noreferrer"
              target="_blank"
            >
              {t('common.read_more')}
            </a>
          </h3>
        </Label>
        <Label className="cinema-rent__terms-checkbox">
          <Checkbox
            id="informed"
            value={terms.input.value?.includes('informed')}
            checked={terms.input.value?.includes('informed')}
            onChange={() => handleTermsChange('informed')}
          />
          <h3>{t('cinema_rent_inquiry_form.yes_informed')}</h3>
        </Label>
        {submitFailed && (terms.meta?.error || validatingTermsConditions(terms)) && (
          <Error>
            {(terms.meta?.error && t(terms.meta?.error)) || t('common.select_value_error')}
          </Error>
        )}
      </div>
      <h3 className="cinema-rent__required-fields">
        * {t('cinema_rent_inquiry_form.required_fields')}
      </h3>
    </div>
  );
};

AdditionalInfo.propTypes = propTypes;
AdditionalInfo.defaultProps = defaultProps;

export default memo(AdditionalInfo);
